import * as yup from 'yup';

import {
  AIRPORT,
  CARGO_TYPES,
  DISTANCE_UNITS,
  MODE_AIR,
  MODE_INTERMODAL,
  MODE_ROAD,
  MODE_WATER,
  PLACE_CITY,
  PLACE_COUNTRY,
  PLACE_GENERIC,
  PLACE_PREMISE,
  SEAPORT,
  VEHICLE_TYPES,
} from '@/lib/constants';
import { LocationModel } from '@/lib/models/location/types';
import { ShipmentCargo } from '@/lib/models/shipment/types';
import {
  AutocompletePlaceTypes,
  EmissionsCargoItemInterface,
  EmissionsSegmentInterface,
  EmissionsVehicles,
  LocationInterface,
} from '@/types';
import {
  EmissionsCalculatorRequestParams,
  EmissionsCalculatorSegmentRequestParams,
  EmissionsLocationRequestParams,
} from '@/types/api-request-types';
import { PlaceInterface, SegmentModeType } from '@/types/api-types';
import { convertVehicleTypeToModeType } from '@/utils/format/mode';
import { getPreferredIdentifier } from '@/utils/helpers';
import { convertMilesToKm } from '@/utils/helpers/convert-units';

import { EMISSIONS_CALC_FORM_FIELDS } from '../../constants';

/**
 * Gets the location identifier for a segment, using either the provided identifier
 * or constructing one from lat/lng coordinates for non-port locations
 */
const getLocationIdentifier = (location?: LocationInterface): EmissionsLocationRequestParams | undefined => {
  if (!location) return undefined;

  if (location.identifier && location.identifier.type !== 'canonical')
    return {
      id: `${location.identifier.type} ${location.identifier.value}`,
      name: location.name, // @NOTE: The backend doesn't need (or use) this in this case, but we need it for the frontend to generate the report name
    };
  if (location.location?.lat && location.location?.lng) {
    return {
      id: `latlng ${location.location.lat},${location.location.lng}`,
      name: location.name, // We need this here so the backend can pass it back to us
    };
  }
  return undefined;
};

/**
 * Gets the appropriate label for a location input based on segment position and state
 */
export const getLocationLabel = (
  segmentIndex: number,
  hasMultipleSegments: boolean,
  isLastSegment: boolean,
): string => {
  if (segmentIndex === 0) return 'Where did the shipment start?';
  if (hasMultipleSegments) return 'Destination';
  if (isLastSegment) return 'Where did it get transported to next?';
  return 'Destination';
};

export const getVehicleTypeLabel = (segmentIndex: number) => {
  if (segmentIndex === 0) return 'What vehicle did your shipment start with?';
  return 'Select a vehicle to add another shipment leg';
};

/**
 * Calculates segment state information from the form data
 */
export const getSegmentState = (segments: EmissionsSegmentInterface[]) => {
  const segmentsWithOrigins = segments?.filter((segment) => segment.origin)?.length || 0;
  const hasMultipleSegments = segmentsWithOrigins >= 2;

  return {
    segmentsWithOrigins,
    hasMultipleSegments,
  };
};

export const getSegmentVehicleType = (segments: EmissionsSegmentInterface[], segmentIndex: number) => {
  return segments?.[segmentIndex]?.vehicle?.type;
};

export const getSegmentLocation = (segments: EmissionsSegmentInterface[], segmentIndex: number) => {
  return segments?.[segmentIndex]?.origin;
};

export const prependTypeToVehicleId = (vehicleType: string, vehicleId: string) => {
  if (!vehicleId) return '';

  if (VEHICLE_TYPES.VESSEL === vehicleType) {
    return `vesselImo ${vehicleId}`;
  }

  if (VEHICLE_TYPES.AIRCRAFT === vehicleType) {
    return `iata ${vehicleId}`;
  }

  return vehicleId;
};

/**
 * Transforms the cargo items from the form state into a format
 * that can be used by the emissions calculator
 */
export const getTransformedCargo = (cargo: EmissionsCargoItemInterface[]): ShipmentCargo[] => {
  return cargo.map((item) => ({
    type: item.type,
    weight: {
      unit: item.weightUnit,
      value: Number(item.totalWeight),
    },
    quantity: item.quantity,
    ...(item.type === CARGO_TYPES.CONTAINER && {
      container: {
        size: `${item.containerSize}'`,
        type: item.isRefrigerated ? 'Reefer' : 'Dry',
      },
    }),
  }));
};

/**
 * Transforms the segments from the form state into a format
 * that can be used by the emissions calculator
 */
export const getTransformedSegments = (
  segments: EmissionsSegmentInterface[],
): EmissionsCalculatorSegmentRequestParams[] => {
  // Transform segments without filtering by location count
  const transformedSegments = segments
    .filter((segment) => segment.vehicle.type !== null)
    .map((segment, index) => {
      const nextSegmentOrigin = segments[index + 1]?.origin;
      const vehicleType = segment.vehicle.type;

      // Filter out stop offs that don't have both name and a valid identifier/location
      const validStopOffs = segment.stopOffs.filter(
        (stopOff) => stopOff.name && (stopOff.identifier || stopOff.location),
      );

      // Build locations array with valid stop offs
      const locations = [
        getLocationIdentifier(segment.origin),
        ...validStopOffs.map((stopOff) => getLocationIdentifier(stopOff)),
        getLocationIdentifier(nextSegmentOrigin),
      ].filter((location): location is EmissionsLocationRequestParams => location !== undefined);

      // Convert distance if in miles
      const distance =
        segment.distanceUnit === DISTANCE_UNITS.MI ? convertMilesToKm(segment.distance) : segment.distance;

      const distanceAsNumber = segment.distance ? Number(distance) : undefined;

      return {
        vehicle: {
          type: vehicleType ? convertVehicleTypeToModeType(vehicleType) : MODE_ROAD,
          vehicleId: vehicleType ? prependTypeToVehicleId(vehicleType, segment.vehicle.vehicleId) : '',
          flightNumber: segment.vehicle.flightNumber || undefined,
        },
        distance: distanceAsNumber,
        locations,
      };
    });

  // Get segments that have at least 2 locations (valid segments)
  return transformedSegments.filter((segment) => segment.locations.length >= 2);
};

export const formatLocationDisplay = (name: string) => {
  const wrapWithError = (content: React.ReactNode) => (
    <span className="flex items-center gap-2">
      {content}
      <span className="location-display-error-message ml-1 hidden bg-forms-states-error px-1.5 py-0.5 text-sm font-normal text-white" />
    </span>
  );

  // First check if it's a lat/lng format
  const latLngMatch = name.match(/^(-?\d+\.\d+),\s*(-?\d+\.\d+)$/);
  if (latLngMatch) {
    const [, lat, lng] = latLngMatch;
    const formattedLat = Number(lat).toFixed(4);
    const formattedLng = Number(lng).toFixed(4);
    return wrapWithError(
      <span className="rounded-sm bg-grey-100 px-1.5 py-1 text-sm">
        {formattedLat}, {formattedLng}
      </span>,
    );
  }

  // Then handle the regular format with brackets
  const match = name.match(/(.*?)\s*\((.*?)\)\s*$/);
  if (match) {
    const [, baseName, code] = match;
    return wrapWithError(
      <>
        <span className="rounded-sm bg-grey-100 px-1.5 py-1 text-sm uppercase">{code}</span>
        <span>{baseName}</span>
      </>,
    );
  }

  return wrapWithError(<span>{name}</span>);
};

export const determineVehicleType = (originType: string, destinationType: string): EmissionsVehicles => {
  // If either location is a city or country, it's always a truck route
  const isCityOrCountry = [originType, destinationType].some(
    (type) => type === PLACE_CITY || type === PLACE_COUNTRY || type === PLACE_PREMISE,
  );
  if (isCityOrCountry) return VEHICLE_TYPES.TRUCK;

  // For non-city/country routes, prioritize based on origin type
  switch (originType) {
    case AIRPORT:
      return VEHICLE_TYPES.AIRCRAFT;
    case SEAPORT:
      return VEHICLE_TYPES.VESSEL;
    default:
      return VEHICLE_TYPES.TRUCK;
  }
};

export const getActiveSegments = (
  segments: EmissionsSegmentInterface[] | undefined | null,
): EmissionsSegmentInterface[] => {
  if (!segments || segments.length === 0) {
    return [];
  }

  // Ignore the last segment (it's a placeholder)
  return segments.slice(0, -1);
};

/**
 * Updates vehicle types for segments based on their locations
 * This is used when segments are added, removed, or modified
 * to ensure vehicle types are correctly set
 */
export const updateSegmentVehicleTypes = (
  segments: EmissionsSegmentInterface[],
  setValue: (name: string, value: any) => void,
) => {
  if (!segments || segments.length === 0) {
    return;
  }

  const activeSegments = getActiveSegments(segments);

  activeSegments.forEach((segment, index) => {
    // Get the next segment's origin (destination of current segment)
    const nextSegment = segments[index + 1];

    try {
      // If both current and next segment have origins, determine vehicle type
      if (segment?.origin?.type && nextSegment?.origin?.type) {
        const vehicleType = determineVehicleType(segment.origin.type, nextSegment.origin.type);
        setValue(`segments.${index}.vehicle`, {
          type: vehicleType,
          vehicleId: '',
        });

        // If the vehicle type is a truck, clear the stop offs
        // as they are not applicable for truck routes
        if (vehicleType === VEHICLE_TYPES.TRUCK) {
          setValue(`segments.${index}.stopOffs`, []);
        }
      } else {
        // If there's no valid origin pair, clear the vehicle type
        setValue(`segments.${index}.vehicle`, {
          type: null,
          vehicleId: '',
        });
      }
    } catch (error) {
      console.error(`Error updating vehicle type for segment ${index}:`, error);
      setValue(`segments.${index}.vehicle`, {
        type: null,
        vehicleId: '',
      });
    }
  });
};

/**
 * Validates that the data has the required shape for a valid API call
 */
export const validateEmissionsDataShape = (
  segments: EmissionsCalculatorRequestParams['segments'] = [],
  cargo: EmissionsCalculatorRequestParams['cargo'] = [],
): boolean => {
  if (!segments.length || !cargo.length) return false;
  const hasInvalidCargo = cargo.some((item) => {
    if (!item.quantity || item.quantity <= 0) return true;

    return false;
  });

  if (hasInvalidCargo) return false;

  const hasInvalidSegment = segments.some((segment) => {
    if (!segment.vehicle?.type) return true;

    if (!segment.locations || segment.locations.length < 2) return true;

    const hasProblematicLocation = segment.locations.some((location) => location.id?.startsWith('unknown'));

    return hasProblematicLocation;
  });

  return !hasInvalidSegment;
};

interface ValidationOptions {
  index?: number;
  context?: {
    value?: {
      segments?: EmissionsSegmentInterface[];
    };
    segments?: EmissionsSegmentInterface[];
  };
}

export const validateOrigin = function validateOriginFn(
  this: yup.TestContext,
  origin: any,
): boolean | yup.ValidationError {
  const options = this.options as ValidationOptions;
  const segments = options.context?.segments || [];
  const currentIndex = options.index ?? -1;

  // Skip validation for the last segment (it's a placeholder)
  if (segments && currentIndex === segments.length - 1) {
    return true;
  }

  if (!origin) {
    return this.createError({ message: 'Location is required' });
  }

  if (origin.identifier && (origin.identifier as any).type === 'unknown') {
    return this.createError({
      message: 'Location needed',
      path: `${this.path}.identifier.type`,
    });
  }

  return true;
};

export const validateStopOff = function validateStopOffFn(
  this: yup.TestContext,
  stopOff: any,
): boolean | yup.ValidationError {
  if (!stopOff) return true;

  const options = this.options as ValidationOptions;
  const currentIndex = options.index ?? -1;
  const indexMsg = currentIndex ? ` ${currentIndex}` : '';

  if (!stopOff.name) {
    return this.createError({
      message: `Stop-off${indexMsg} location is required`,
      path: `${this.path}.name`,
    });
  }

  if (stopOff.identifier && (stopOff.identifier as any).type === 'unknown') {
    return this.createError({
      message: `Stop-off${indexMsg} has an unknown location type`,
      path: `${this.path}.identifier.type`,
    });
  }

  return true;
};

export const validateVehicle = function validateVehicleFn(
  this: yup.TestContext,
  vehicle: any,
): boolean | yup.ValidationError {
  const options = this.options as ValidationOptions;
  const segments = options.context?.value?.segments || [];
  const currentIndex = options.index ?? -1;

  // Skip validation if no segments array or couldn't extract index
  if (!segments || currentIndex === -1) {
    return true;
  }

  // Skip validation for placeholder segments (always the last one)
  if (currentIndex === segments.length - 1) {
    return true;
  }

  const thisSegment = segments[currentIndex];
  const nextSegment = segments[currentIndex + 1];

  const isCompleteSegment = thisSegment?.origin && nextSegment?.origin;

  if (!isCompleteSegment) {
    return true;
  }

  if (!vehicle?.type) {
    return this.createError({
      message: 'Vehicle type is required',
      path: `${this.path}.type`,
      type: 'vehicle-type-required',
    });
  }

  return true;
};

export const validateConsecutiveSegments = function validateConsecutiveSegmentsFn(
  this: yup.TestContext,
  segments: any,
): boolean | yup.ValidationError {
  if (!segments) return true;

  // Skip the last segment as it's a placeholder
  const activeSegments = getActiveSegments(segments);

  // Find first pair of consecutive duplicates
  const duplicateIndex = activeSegments.findIndex((segment, index) => {
    // Skip last segment in our check
    if (index === activeSegments.length - 1) return false;

    const currentOrigin = segment.origin;
    const nextOrigin = activeSegments[index + 1]?.origin;

    // Skip if either origin is missing
    if (!currentOrigin || !nextOrigin) return false;

    return currentOrigin.name === nextOrigin.name && currentOrigin.identifier?.value === nextOrigin.identifier?.value;
  });

  if (duplicateIndex !== -1) {
    const currentOrigin = activeSegments[duplicateIndex].origin;
    return this.createError({
      message: `Cannot have the same location (${currentOrigin?.name}) in consecutive segments`,
      path: `segments[${duplicateIndex + 1}].origin`,
      type: 'duplicate-location',
    });
  }

  return true;
};

const cargoItemSchema = yup.object({
  [EMISSIONS_CALC_FORM_FIELDS.CARGO_TYPE]: yup.string().required('Select a cargo option'),
  [EMISSIONS_CALC_FORM_FIELDS.QUANTITY]: yup
    .number()
    .required('Enter a quantity')
    .min(1, 'Cannot be less than 1')
    .typeError('Enter a valid number for the quantity'),
  [EMISSIONS_CALC_FORM_FIELDS.CONTAINER_SIZE]: yup.string().when(EMISSIONS_CALC_FORM_FIELDS.CARGO_TYPE, {
    is: (cargoType: string) => cargoType === CARGO_TYPES.CONTAINER,
    then: yup.string().required('Select a container size'),
  }),
  [EMISSIONS_CALC_FORM_FIELDS.TOTAL_WEIGHT]: yup
    .number()
    .required('Enter a total weight')
    .min(1, 'Cannot be less than 1')
    .typeError('Enter a valid number for the total weight'),
});

export const emissionsCalculatorValidationSchema = yup.object({
  cargoItems: yup.array().of(cargoItemSchema).min(1, 'At least one cargo item is required'),
  segments: yup
    .array()
    .min(1, 'At least one segment is required')
    .test(
      'no-consecutive-duplicates',
      'Consecutive segments cannot have the same location',
      validateConsecutiveSegments,
    )
    .of(
      yup.object({
        origin: yup.object().nullable().test('valid-origin', 'Location is required', validateOrigin),
        stopOffs: yup.array().of(yup.object().test('valid-stop-off', 'Stop off must be valid', validateStopOff)),
        vehicle: yup
          .object({
            type: yup.string().nullable(),
          })
          .test('vehicle-type-required', 'Vehicle type is required for this segment', validateVehicle),
      }),
    ),
});

interface SegmentPlaceConfig {
  placeTypes: AutocompletePlaceTypes;
  placeholder: string;
}

type LocationType = 'segment' | 'stopOff';

export const getPlaceTypesAndPlaceholders = ({
  locationType = 'segment',
  currentSegmentVehicleType,
  previousSegmentVehicleType = '',
  mode,
}: {
  locationType?: LocationType;
  currentSegmentVehicleType: string;
  previousSegmentVehicleType?: string;
  mode: string;
}): SegmentPlaceConfig => {
  const getConfig = (types: AutocompletePlaceTypes, label: string): SegmentPlaceConfig => ({
    placeTypes: types,
    placeholder: `Enter ${label}`,
  });

  const airConfig = getConfig([AIRPORT], AIRPORT);
  const seaportConfig = getConfig([SEAPORT], SEAPORT);

  // Mode-specific configs with additional place types
  const airModeConfig = getConfig([AIRPORT, PLACE_GENERIC], `address or ${AIRPORT}`);
  const seaModeConfig = getConfig([SEAPORT, PLACE_GENERIC], `address or ${SEAPORT}`);

  const defaultModeConfig = mode === MODE_AIR ? airModeConfig : seaModeConfig;

  // Stop-offs
  if (locationType === 'stopOff') {
    if (currentSegmentVehicleType === VEHICLE_TYPES.AIRCRAFT) {
      return airConfig;
    }

    if (currentSegmentVehicleType === VEHICLE_TYPES.VESSEL) {
      return seaportConfig;
    }

    // Default for stop-offs based on mode (no PLACE_GENERIC)
    return mode === MODE_AIR ? airConfig : seaportConfig;
  }

  // Segment locations
  if (!previousSegmentVehicleType) {
    if (currentSegmentVehicleType === VEHICLE_TYPES.AIRCRAFT) {
      return airConfig;
    }
    if (currentSegmentVehicleType === VEHICLE_TYPES.VESSEL) {
      return seaportConfig;
    }
    return defaultModeConfig;
  }

  if (currentSegmentVehicleType === VEHICLE_TYPES.VESSEL || previousSegmentVehicleType === VEHICLE_TYPES.VESSEL) {
    return seaportConfig;
  }

  if (currentSegmentVehicleType === VEHICLE_TYPES.AIRCRAFT || previousSegmentVehicleType === VEHICLE_TYPES.AIRCRAFT) {
    return airConfig;
  }

  return defaultModeConfig;
};

export const generateReportName = (segments: EmissionsCalculatorSegmentRequestParams[]) => {
  if (!segments || segments.length === 0) {
    return '';
  }

  const firstSegment = segments[0];
  const firstSegmentLocation = firstSegment.locations?.[0] || null;
  const lastSegment = segments.slice(-1)[0];
  const lastSegmentLocation = lastSegment ? lastSegment.locations[lastSegment.locations.length - 1] : null;

  if (!firstSegmentLocation || !lastSegmentLocation) {
    return '';
  }

  const firstSegmentName = firstSegmentLocation.name || '';
  const lastSegmentName = lastSegmentLocation.name || '';

  if (!firstSegmentName || !lastSegmentName) {
    return '';
  }

  return `${firstSegmentName} to ${lastSegmentName}`;
};

/**
 * Converts a mode to a form mode
 * @TODO: Consider using MODE_WATER for the form mode
 * so we don't need this
 */
export const convertModeToFormMode = (mode: SegmentModeType) => {
  switch (mode) {
    case MODE_AIR:
      return MODE_AIR;
    default:
      return MODE_INTERMODAL;
  }
};

export const convertModeToVehicleType = (mode: SegmentModeType): EmissionsVehicles | null => {
  switch (mode) {
    case MODE_WATER:
      return VEHICLE_TYPES.VESSEL;
    case MODE_AIR:
      return VEHICLE_TYPES.AIRCRAFT;
    default:
      return VEHICLE_TYPES.TRUCK;
  }
};

export const convertLocationToPlaceFormat = (location: LocationModel): PlaceInterface => {
  // Extract names from the location object
  const shortName = location.names?.short?.[0] || '';
  const longName = location.names?.long?.[0] || '';
  const name = longName || shortName;

  const identifier = getPreferredIdentifier(location.identifiers);

  return {
    from: 'typesense' as const,
    type: location.type,
    name,
    shortName,
    countryCode: location.countryCode || '',
    placeId: location.id || '', // Use the id as placeId
    location: location.location || { lat: 0, lng: 0 },
    identifier,
  };
};
